import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Inbox, Pencil, Plus, Trash2 } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, EmptyState, PageHeader, DataTable, ui } from "../../../components/ui";
import { Button } from "../../../components/ui/button";
import { LeaveActionToast } from "../../leave-management/components";
import { ShiftStatusBadge, DeleteShiftModal, ShiftFilterBar } from "../components";
import { isOvernightShift, toHHMM } from "../utils/shiftTimeUtils";
import {
  fetchAllShifts,
  fetchActiveShiftCount,
  fetchShiftsByOvertime,
  fetchShiftsByCompOff,
  fetchShiftsByApprovalFlow,
  updateShiftOnServer,
  fetchActiveShifts,
  checkShiftAssignment } from
"../../../services/shiftService";

const TOAST_KEY = "organization_shifts_toast";

function ShiftsTableSkeleton() {
  return (
    <div className="px-6 py-8">
      <div className="flex flex-col gap-3">
        {[1, 2, 3, 4].map((n) =>
        <div key={n} className="h-11 rounded-lg bg-[var(--color-workspace)]/50 animate-pulse" />
        )}
      </div>
    </div>);

}

export function ShiftsListPage() {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [overtimeFilter, setOvertimeFilter] = useState("All");
  const [compOffFilter, setCompOffFilter] = useState("All");
  const [approvalFlowFilter, setApprovalFlowFilter] = useState("All");
  const [activeCount, setActiveCount] = useState(0);
  const [activeCountLoading, setActiveCountLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalCountLoading, setTotalCountLoading] = useState(true);
  const [deletingShift, setDeletingShift] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [checkingDeleteId, setCheckingDeleteId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [toast, setToast] = useState(null);

  const triggerToast = (message, type = "success") => setToast({ message, type });

  useEffect(() => {
    const raw = sessionStorage.getItem(TOAST_KEY);
    if (!raw) return;
    try {
      setToast(JSON.parse(raw));
    } catch {
      setToast(null);
    }
    sessionStorage.removeItem(TOAST_KEY);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const loadShifts = useCallback(() => {
    setLoading(true);

    const noOtherFilterApplied =
    overtimeFilter === "All" && compOffFilter === "All" && approvalFlowFilter === "All";

    const useActiveEndpoint = statusFilter === "Active" && noOtherFilterApplied;

    let request;
    if (useActiveEndpoint) request = fetchActiveShifts();else
    if (overtimeFilter !== "All") request = fetchShiftsByOvertime(overtimeFilter === "true");else
    if (compOffFilter !== "All") request = fetchShiftsByCompOff(compOffFilter === "true");else
    if (approvalFlowFilter !== "All") request = fetchShiftsByApprovalFlow(approvalFlowFilter);else
    request = fetchAllShifts();

    request
    .then((data) => {
      const list = Array.isArray(data) ? data : data?.data ?? [];
      const filtered =
      useActiveEndpoint || statusFilter === "All" ?
      list :
      list.filter((s) => s.isActive === (statusFilter === "Active"));
      setShifts(filtered);
    })
    .catch(() => setShifts([]))
    .finally(() => setLoading(false));
  }, [approvalFlowFilter, compOffFilter, overtimeFilter, statusFilter]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  useEffect(() => {
    setActiveCountLoading(true);
    fetchActiveShiftCount()
    .then(setActiveCount)
    .catch(() => setActiveCount(0))
    .finally(() => setActiveCountLoading(false));

    setTotalCountLoading(true);
    fetchAllShifts()
    .then((list) => setTotalCount(list.length))
    .catch(() => setTotalCount(0))
    .finally(() => setTotalCountLoading(false));
  }, []);

  const inactiveCount = totalCountLoading || activeCountLoading ? "—" : totalCount - activeCount;
  const filtered = useMemo(() => [...shifts].sort((a, b) => a.shiftName.localeCompare(b.shiftName)), [shifts]);

  const openCreatePage = () => navigate("/hrms-team/config/shifts/create");
  const openEditPage = (shift) => navigate(`/hrms-team/config/shifts/edit/${String(shift.id)}`);
  const openDetailPage = (shift) => navigate(`/hrms-team/config/shifts/${String(shift.id)}`);

  const handleDeleteClick = async (shift) => {
    setCheckingDeleteId(shift.id);
    try {
      const { assignedEmployeeCount } = await checkShiftAssignment(shift.id);
      setDeletingShift({ ...shift, assignedEmployeeCount });
    } catch {
      triggerToast("Failed to check shift usage. Please try again.", "error");
    } finally {
      setCheckingDeleteId(null);
    }
  };

  const executeDeletion = async () => {
    if (!deletingShift) return;
    setDeleting(true);
    try {
      await updateShiftOnServer({ ...deletingShift, isActive: false });
      setShifts((prev) => prev.filter((s) => s.id !== deletingShift.id));
      setActiveCount((prev) => deletingShift.isActive ? prev - 1 : prev);
      setTotalCount((prev) => prev - 1);
      triggerToast(`Shift "${deletingShift.shiftName}" deleted and removed from the list.`);
      setDeletingShift(null);
    } catch {
      triggerToast("Failed to delete. Please try again.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (shift) => {
    setTogglingId(shift.id);
    const nextIsActive = !shift.isActive;
    try {
      await updateShiftOnServer({ ...shift, isActive: nextIsActive });
      setShifts((prev) => prev.map((s) => s.id === shift.id ? { ...s, isActive: nextIsActive } : s));
      setActiveCount((prev) => prev + (nextIsActive ? 1 : -1)); // <-- add this
      triggerToast(`Shift "${shift.shiftName}" marked ${nextIsActive ? "Active" : "Inactive"}.`);
    } catch {
      triggerToast("Failed to update shift status. Please try again.", "error");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <AppLayout
      overlay={
      <>
          <LeaveActionToast toast={toast} />
          <DeleteShiftModal
          deletingShift={deletingShift}
          setDeletingShift={setDeletingShift}
          executeDeletion={executeDeletion}
          deleting={deleting} />
        
        </>
      }>
      
      <PageBackLink to="/" />
      <PageHeader
        title={i18n.t("organizationShifts.organizationShifts")}
        action={
        <Button type="button" onClick={openCreatePage} className={ui.primaryBtn}>
            <Plus className="h-4 w-4" />
            <Trans i18nKey="formActions.createShift" />
          </Button>
        } />
      

      <ShiftFilterBar
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        overtimeFilter={overtimeFilter}
        setOvertimeFilter={setOvertimeFilter}
        compOffFilter={compOffFilter}
        setCompOffFilter={setCompOffFilter}
        approvalFlowFilter={approvalFlowFilter}
        setApprovalFlowFilter={setApprovalFlowFilter}
        allCount={totalCountLoading ? "—" : totalCount}
        activeCount={activeCountLoading ? "—" : activeCount}
        inactiveCount={inactiveCount} />
      

      <ContentCard>
        {loading ?
        <ShiftsTableSkeleton /> :
        filtered.length === 0 ?
        <EmptyState
          icon={Inbox}
          title={i18n.t("organizationShifts.noShiftsFound")}
          message="Try adjusting filters or create a new shift." /> :


        <>
            {/* Desktop View using shared UI DataTable primitive */}
            <DataTable
            minWidth="920px"
            columns={[
            { key: "name", label: "Shift Name" },
            { key: "start", label: "Shift Start Time" },
            { key: "end", label: "Shift End Time" },
            { key: "active", label: "Is Active" },
            { key: "ot", label: "Overtime Enabled" },
            { key: "comp", label: "Comp Off Enabled" },
            { key: "actions", label: "Actions", className: "text-right" }]
            }>
            
              {filtered.map((shift) =>
            <tr
              key={shift.id}
              onClick={() => openDetailPage(shift)}
              className="hover:bg-[var(--color-workspace)]/20 transition-colors cursor-pointer">
              
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground text-sm">{shift.shiftName}</span>
                      {isOvernightShift(shift.shiftStartTime, shift.shiftEndTime) &&
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[var(--color-accent)]/10 text-[var(--color-accent)] text-[10px] font-semibold whitespace-nowrap">
                          <Trans i18nKey="pagination.nextDay" />
                        </span>
                  }
                    </div>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-sm text-foreground">{toHHMM(shift.shiftStartTime)}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-sm text-foreground">{toHHMM(shift.shiftEndTime)}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <button
                  type="button"
                  onClick={() => handleToggleActive(shift)}
                  disabled={togglingId === shift.id}
                  className="inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={`Mark shift ${shift.isActive ? "Inactive" : "Active"}`}>
                  
                      <span
                    className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
                    shift.isActive ? "bg-[var(--color-primary)]" : "bg-gray-200"}`
                    }>
                    
                        <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                      shift.isActive ? "left-[18px]" : "left-0.5"}`
                      } />
                    
                      </span>
                      <ShiftStatusBadge status={shift.isActive ? "Active" : "Inactive"} />
                    </button>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-sm">
                    {shift.overtimeEnabled ?
                <span className="text-[var(--color-success)] font-semibold"><Trans i18nKey="common.yes" /></span> :

                <span className="text-[var(--color-text-secondary)]"><Trans i18nKey="common.no" /></span>
                }
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-sm">
                    {shift.compOffEnabled ?
                <span className="text-[var(--color-success)] font-semibold"><Trans i18nKey="common.yes" /></span> :

                <span className="text-[var(--color-text-secondary)]"><Trans i18nKey="common.no" /></span>
                }
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                    type="button"
                    variant="outline"
                    onClick={() => openDetailPage(shift)}
                    className="h-8 w-8 p-0 rounded-lg border-[var(--color-text-border)]"
                    aria-label={i18n.t("formActions.viewShift")}>
                    
                        <Eye className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" />
                      </Button>
                      <Button
                    type="button"
                    variant="outline"
                    onClick={() => openEditPage(shift)}
                    className="h-8 w-8 p-0 rounded-lg border-[var(--color-text-border)]"
                    aria-label={i18n.t("formActions.editShift")}>
                    
                        <Pencil className="h-3.5 w-3.5 text-[var(--color-primary)]" />
                      </Button>
                      <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleDeleteClick(shift)}
                    disabled={checkingDeleteId === shift.id}
                    className="h-8 w-8 p-0 rounded-lg border-[var(--color-text-border)] disabled:opacity-50"
                    aria-label={i18n.t("formActions.deleteShift")}>
                    
                        <Trash2 className="h-3.5 w-3.5 text-[var(--color-error)]" />
                      </Button>
                    </div>
                  </td>
                </tr>
            )}
            </DataTable>

            {/* Mobile View responsive card layout */}
            <div className="md:hidden space-y-4 p-4">
              {filtered.map((shift) =>
            <div
              key={shift.id}
              onClick={() => openDetailPage(shift)}
              className="p-4 rounded-xl border border-[var(--color-text-border)]/50 bg-white shadow-sm flex flex-col gap-3 cursor-pointer hover:border-[var(--color-primary)]/40 transition-colors">
              
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className="font-semibold text-foreground text-base flex items-center gap-2 flex-wrap">
                        {shift.shiftName}
                        {isOvernightShift(shift.shiftStartTime, shift.shiftEndTime) &&
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[var(--color-accent)]/10 text-[var(--color-accent)] text-[10px] font-semibold">
                            <Trans i18nKey="pagination.nextDay" />
                          </span>
                    }
                      </h4>
                      <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                        <Trans i18nKey="organizationShifts.timing" /> {toHHMM(shift.shiftStartTime)} — {toHHMM(shift.shiftEndTime)}
                      </p>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <button
                    type="button"
                    onClick={() => handleToggleActive(shift)}
                    disabled={togglingId === shift.id}
                    className="inline-flex items-center gap-1.5"
                    aria-label={`Toggle shift active status for ${shift.shiftName}`}>
                    
                       <span
                      className={`relative w-8 h-[18px] rounded-full transition-colors shrink-0 ${
                      shift.isActive ? "bg-[var(--color-primary)]" : "bg-gray-200"}`
                      }>
                      
                          <span
                        className={`absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow transition-all ${
                        shift.isActive ? "left-[16px]" : "left-0.5"}`
                        } />
                      
                        </span>
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs border-t border-[var(--color-text-border)]/35 pt-2 text-[var(--color-text-secondary)]">
                    <div>
                      <Trans i18nKey="organizationShifts.ot" />{" "}
                      {shift.overtimeEnabled ?
                  <span className="text-[var(--color-success)] font-semibold"><Trans i18nKey="common.yes" /></span> :

                  "No"
                  }
                    </div>
                    <div>
                      <Trans i18nKey="organizationShifts.compOff2" />{" "}
                      {shift.compOffEnabled ?
                  <span className="text-[var(--color-success)] font-semibold"><Trans i18nKey="common.yes" /></span> :

                  "No"
                  }
                    </div>
                    <div className="ml-auto">
                      <ShiftStatusBadge status={shift.isActive ? "Active" : "Inactive"} />
                    </div>
                  </div>

                  <div
                className="flex items-center justify-end gap-2 border-t border-[var(--color-text-border)]/35 pt-2 mt-1"
                onClick={(e) => e.stopPropagation()}>
                
                    <Button
                  type="button"
                  variant="outline"
                  onClick={() => openDetailPage(shift)}
                  className="h-8 px-3 text-xs rounded-lg border-[var(--color-text-border)]">
                  
                      <Eye className="h-3.5 w-3.5 mr-1 text-[var(--color-text-secondary)]" />
                      <Trans i18nKey="formActions.view" />
                    </Button>
                    <Button
                  type="button"
                  variant="outline"
                  onClick={() => openEditPage(shift)}
                  className="h-8 px-3 text-xs rounded-lg border-[var(--color-text-border)]">
                  
                      <Pencil className="h-3.5 w-3.5 mr-1 text-[var(--color-primary)]" />
                      <Trans i18nKey="common.edit" />
                    </Button>
                    <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleDeleteClick(shift)}
                  disabled={checkingDeleteId === shift.id}
                  className="h-8 px-3 text-xs rounded-lg border-[var(--color-text-border)] text-[var(--color-error)]">
                  
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      <Trans i18nKey="common.delete" />
                    </Button>
                  </div>
                </div>
            )}
            </div>
          </>
        }
      </ContentCard>
    </AppLayout>);

}

export { TOAST_KEY };
